import styled from "@emotion/styled";
import {
  Button,
  FlexRow,
  H2,
  Img,
  Link,
  LoadingSpinner,
  ScreenReaderText,
  SiteContainer,
  Text,
} from "atoms";
import gem from "assets/images/roadmap_game.svg";
import raffleTicket from "assets/images/raffle-ticket.png";
import IconError from "atoms/icons/error.svg";
import { isDev } from "config";
import { useAuth, User } from "features/auth";
import { FeaturedImage } from "features/images";
import { useProfilePoints } from "features/profile";
import {
  MoreItems,
  SortedRaffleItems,
  useClaimRaffle,
  useRaffleItems,
  useRaffleItemsResort,
} from "features/pointsStore";
import { ApiError } from "utils/api";

function RewardsShop() {
  const { user } = useAuth();
  const { data: profilePoints } = useProfilePoints();

  const { data } = useRaffleItems();

  return (
    <>
      <HeaderWrapper>
        <ScreenReaderText as="h1">Hug points perks</ScreenReaderText>
      </HeaderWrapper>

      <SiteContainer width="md">
        {data && user ? (
          <RewardsShopInner
            raffles={data}
            user={user}
            balance={profilePoints?.pointsBalance ?? 0}
          />
        ) : (
          <LoadingSpinner />
        )}
      </SiteContainer>
    </>
  );
}

type RewardsShopInnerProps = {
  raffles: SortedRaffleItems;
  balance: number;
  user: User;
};

function RewardsShopInner({ raffles, balance, user }: RewardsShopInnerProps) {
  // HACK: If in dev environment, fake having a current raffle for testing
  const needFake = isDev && !raffles.current && raffles.future.length;
  const fakeCurrent = {
    past: raffles.past,
    current: raffles.future[0],
    future: raffles.future.slice(1),
  };

  const { now, current, past, future } = useRaffleItemsResort(
    needFake ? fakeCurrent : raffles
  );

  const { isEntered, mutation } = useClaimRaffle(current?.id);

  const claimRaffle = () => mutation.mutate();

  let mutationError;
  if (mutation.isError) {
    if (mutation.error instanceof ApiError) {
      mutationError = mutation.error.message;
      if (mutation.error.message.includes("email")) {
        mutationError = (
          <>
            Looks like you need to{" "}
            <Link to="/profile/edit">add your email to your profile</Link> first
          </>
        );
      } else if (mutation.error.message.includes("points")) {
        mutationError =
          "Sorry, you don't have enough HUG Points to enter this raffle";
      } else if (mutation.error.message.includes("entered")) {
        mutationError =
          "Looks like you already entered this raffle so you'll have to wait for the next to open";
      } else if (mutation.error.message.includes("not active")) {
        mutationError =
          "Sorry, that raffle is no longer open. Try refreshing to see the current raffle.";
      }
    } else {
      mutationError = "Something went wrong, try reloading and entering again.";
    }
  }

  return (
    <>
      {current ? (
        <CurrentPerk>
          <PerkTitle>
            <Text size="xs" bold>
              Current Raffle
            </Text>
            <H2 size="h1">{current.title}</H2>
          </PerkTitle>

          <PerkContent>
            {current && <Countdown now={now} end={current.endDate} />}

            <SFlexRow alignItems="center">
              <PriceAndQty size="xs" bold>
                <img src={gem} alt="" width="16" height="16" />

                {current.cost.toLocaleString()}

                <ScreenReaderText as="span">Hug points</ScreenReaderText>
              </PriceAndQty>

              <span>|</span>

              <PriceAndQty size="xs" bold>
                {current.quantity} available
              </PriceAndQty>
            </SFlexRow>

            {isEntered ? (
              <Success>Woohoo! We received your entry for this raffle.</Success>
            ) : (
              <>
                {!user.email && (
                  <Error>
                    <span>
                      You need to{" "}
                      <Link to="/profile/edit">
                        add your email to your profile
                      </Link>{" "}
                      to enter.
                    </span>
                  </Error>
                )}

                {balance < current.cost ? (
                  <Error>
                    You need {(current.cost - balance).toLocaleString()} more
                    HUG Points to enter.
                  </Error>
                ) : (
                  <Button
                    variant="gradient"
                    onClick={claimRaffle}
                    disabled={mutation.isLoading}
                  >
                    Enter Raffle
                  </Button>
                )}

                {mutationError && <Error>{mutationError}</Error>}
              </>
            )}

            <Text size="xxs">
              <br />
              Winners will be randomly selected and notified by email within 2
              days of the raffle&rsquo;s close. HUG Points will only be deducted
              from the winner&rsquo;s account.
            </Text>
          </PerkContent>

          <PerkImage>
            <figure>
              <FeaturedImage src={current.image} alt="" />
              <RaffleTicket src={raffleTicket} alt="" aria-hidden="true" />
            </figure>
          </PerkImage>
        </CurrentPerk>
      ) : (
        <NoPerk>
          {/* <IconClouds aria-hidden="true"/> */}
          <ScreenReaderText as="h1">Hug Points redemption</ScreenReaderText>
          <H2 size="h1">
            Oh no!
            <br />
            There aren&rsquo;t any raffles right&nbsp;now.
          </H2>
          <Text>Check back soon for more raffles.</Text>
        </NoPerk>
      )}

      <MoreItems label="Coming Soon" items={future} />
      <MoreItems label="Just Ended" items={past} />
    </>
  );
}

const headerBottomHeight = "calc(15em + 10vw)";
const HeaderWrapper = styled.header(({ theme }) => ({
  paddingTop: 0,
  position: "relative",
  backgroundImage: `linear-gradient(to right, ${theme.colors.yellow} 0%, ${theme.colors.green} 100%)`,
  "&::after": {
    content: "''",
    position: "absolute",
    left: 0,
    right: 0,
    top: "100%",
    height: headerBottomHeight,
    backgroundImage: `linear-gradient(to bottom , transparent 0%, white 100%), linear-gradient(to right, ${theme.colors.yellow} 0%, ${theme.colors.green} 100%)`,
    pointerEvents: "none",
    zIndex: 1,
  },
  "&+*": {
    paddingTop: "5rem",
    position: "relative",
    zIndex: 3,
  },
}));
const NoPerk = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  h2: {
    marginTop: 10,
  },
}));
const CurrentPerk = styled.div(({ theme }) => ({
  display: "grid",
  gap: "1rem",
  gridTemplateAreas: "'title' 'image' 'content'",
  "@media (min-width: 600px)": {
    gridTemplateRows: "auto auto",
    gridTemplateColumns: "2fr 1fr",
    gridTemplateAreas: "'title image' 'content content'",
  },
  "@media (min-width: 831px)": {
    gap: "3rem",
    gridTemplateAreas: "'title image' 'content image'",
    gridTemplateColumns: "4fr 3fr",
  },
}));
const PerkTitle = styled.div({
  gridArea: "title",
  alignSelf: "center",
  h2: {
    margin: 0,
  },
});
const PerkContent = styled.div(({ theme }) => ({
  gridArea: "content",
}));
const PerkImage = styled.div(({ theme }) => ({
  gridArea: "image",
  figure: {
    display: "block",
    margin: "0 auto",
    maxWidth: "250px",
    position: "relative",
    "@media (min-width: 600px)": {
      marginTop: "1em",
      maxWidth: "100%",
    },
  },
  "img:first-of-type": {
    maxWidth: "100%",
    boxShadow: "0 1px 1em 0 rgb(0 0 0 / .1),0 0 1px 0 rgb(0 0 0 / .15)",
  },
}));
const RaffleTicket = styled(Img)({
  position: "absolute",
  right: 0,
  bottom: 0,
  width: "50%",
  maxWidth: 200,
  transform: "translate(2rem, 15%)",
});
const SFlexRow = styled(FlexRow)({
  paddingBottom: ".5rem",
});
const PriceAndQty = styled(Text)({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  margin: 0,
  fontWeight: "bold",
});

const Error = styled.div(({ theme }) => ({
  color: theme.colors.orangeDark,
  fontSize: theme.fontSizes.xs,
  display: "flex",
  alignItems: "center",
  position: "relative",
  margin: ".5rem 0",
  gap: ".33em",
  "&::before": {
    backgroundImage: `url("${IconError}")`,
    backgroundRepeat: "no-repeat",
    content: "''",
    display: "inline-block",
    height: "1.2em",
    // marginRight: ".3em",
    width: "1.2em",
    filter:
      "brightness(0) saturate(100%) invert(18%) sepia(96%) saturate(2640%) hue-rotate(19deg) brightness(101%) contrast(101%)",
  },
}));

const Success = styled(Text)(({ theme }) => ({
  borderRadius: theme.borderRadius.round,
  backgroundColor: theme.colors.green,
  padding: ".2em 1em",
  fontSize: theme.fontSizes.xs,
  marginTop: ".5rem",
  display: "inline-block",
}));

const msPerSec = 1000;
const msPerMin = 60 * msPerSec;
const msPerHour = 60 * msPerMin;
const msPerDay = 24 * msPerHour;

function Countdown({ now, end }: { now: Date; end: Date }) {
  const remaining = end.getTime() - now.getTime();

  const days = Math.floor(remaining / msPerDay);
  const hours = Math.floor((remaining - days * msPerDay) / msPerHour);
  const mins = Math.floor(
    (remaining - days * msPerDay - hours * msPerHour) / msPerMin
  );
  const secs = Math.floor(
    (remaining - days * msPerDay - hours * msPerHour - mins * msPerMin) /
      msPerSec
  );

  return (
    <>
      <CountdownTitle size="xxs" bold>
        Time remaining to enter
      </CountdownTitle>
      <CountdownTime size="md" bold>
        <span data-value="days">
          {days.toString().padStart(2, "0")}
          <small aria-label="days">d</small>
        </span>
        <span data-value="hours">
          {hours.toString().padStart(2, "0")}
          <small aria-label="hours">h</small>
        </span>
        <span data-value="minutes">
          {mins.toString().padStart(2, "0")}
          <small aria-label="minutes">m</small>
        </span>
        <span data-value="seconds">
          {secs.toString().padStart(2, "0")}
          <small aria-label="seconds">s</small>
        </span>
      </CountdownTime>
      {/* NOTE: Useful for testing
        <Text size="sm">Now: {now.toString()}</Text>
        <Text size="sm">End: {end.toString()}</Text>
      */}
    </>
  );
}
const CountdownTitle = styled(Text)(({ theme }) => ({
  margin: 0,
  opacity: 0.8,
}));
const CountdownTime = styled(Text)(({ theme }) => ({
  display: "inline-flex",
  gap: ".33em",
  letterSpacing: "-.033em",
  fontVariantNumeric: "tabular-nums",
}));

export { RewardsShop };
